import { DateTime } from 'luxon';

import { Session } from '@benzinga/session';
import { mapWithConcurrency } from '@benzinga/safe-await';
import { chunkifyArray } from '@benzinga/utils';

import { QuotesManager, RankingDetail, DelayedQuote } from '@benzinga/quotes-manager';
import { CoinData, CryptoManager, isUSDCryptoSymbol } from '@benzinga/crypto-manager';
import { Financials, SecuritiesManager } from '@benzinga/securities-manager';
import type { WatchlistRelatedSymbol } from '@benzinga/scanner-manager';
import { CompanyDetails, InsiderTradesManager } from '@benzinga/insider-trades-manager';
import { CalendarManager, Ratings } from '@benzinga/calendar-manager';
import { getWatchlistInsiderFilingsFromSecCalendar } from '../../../../src/utils/watchlistSecCalendar';
import { Watchlist, Holding, WatchlistSymbol, normalizeWatchlistSymbol } from '@benzinga/watchlist-manager';
import { ChartManager } from '@benzinga/chart-manager';
import { logger } from '@benzinga/logging';
import { fillMonthlySeriesValues, generateHistoryLineChartOptions, toMonthKey } from './_components/utils';
import {
  allocationLabelFromQuote,
  buildDailyChangePosition,
  getHoldingCostBasis,
  getHoldingQuantity,
  getHistoryChartWeight,
  resolveDailyChangePercent,
  type DailyChangeResult,
} from './_components/portfolioActionUtils';
import {
  getSymbolsMissingRankings,
  getTickerDetailsWithRetry,
  isWatchlistFinancialsDegraded,
  mergeRankingsIntoRows,
  rankingsFromTickerDetails,
} from './_components/watchlistFinancialsResilience';

export type WatchlistFinancials = {
  symbol: string;
  quote?: DelayedQuote | null;
  holdings?: Holding | Holding[] | null;
  financials?: Financials | null;
  rankings?: RankingDetail | null;
  ratings?: Ratings | null;
  cryptoData?: CoinData | null;
  watchlistSymbol?: WatchlistSymbol;
};

export {
  getTickerDetailsWithRetry,
  getSymbolsMissingRankings,
  mergeRankingsIntoRows,
  isWatchlistFinancialsDegraded,
} from './_components/watchlistFinancialsResilience';

const getCryptoBaseSymbol = (symbol: string) => symbol.split('/')[0]?.toUpperCase() ?? symbol;

const symbolsMatch = (a: string, b: string) => normalizeWatchlistSymbol(a) === normalizeWatchlistSymbol(b);

const ratingQuery = (symbols: string[]) => {
  return {
    dateFrom: DateTime.fromJSDate(new Date()).minus({ years: 5 }).toJSDate(),
    fields:
      'fields=id,date,analyst_name,rating_current,analyst, pt_prior,pt_current,action_company,rating_prior,exchange,action_pt,ticker',
    pageSize: 100,
    symbols: symbols.join(','),
  };
};

const SYMBOL_BATCH_SIZE = 50;
const MAX_CONCURRENT_BATCHES = 3;
const WATCHLIST_QUOTES_TTL_MS = 30_000;
const WATCHLIST_ENRICHMENT_TTL_MS = 30_000;

const watchlistQuotesCache = new Map<
  string,
  { expiresAt: number; promise?: Promise<WatchlistFinancials[]>; value?: WatchlistFinancials[] }
>();

const watchlistEnrichmentCache = new Map<
  string,
  { expiresAt: number; promise?: Promise<WatchlistFinancials[]>; value?: WatchlistFinancials[] }
>();

export const getWatchlistFinancialsCacheKey = (watchlist: Pick<Watchlist, 'watchlistId' | 'symbols'>): string => {
  const symbols = [...new Set(watchlist.symbols.map(symbol => symbol.symbol.trim()).filter(Boolean))].sort().join(',');
  return `${watchlist.watchlistId}:${symbols}`;
};

const getWatchlistQuotesCacheKey = (watchlist: Pick<Watchlist, 'watchlistId' | 'symbols'>): string =>
  `quotes:${getWatchlistFinancialsCacheKey(watchlist)}`;

const getWatchlistEnrichmentCacheKey = (watchlist: Pick<Watchlist, 'watchlistId' | 'symbols'>): string =>
  `enrichment:${getWatchlistFinancialsCacheKey(watchlist)}`;

const buildPlaceholderFinancialRows = (symbolChunk: string[], watchlist: Watchlist): WatchlistFinancials[] =>
  symbolChunk.map(symbol => {
    const watchlistSymbol = watchlist.symbols.find(symbolItem => symbolsMatch(symbolItem.symbol, symbol));
    return {
      symbol,
      quote: null,
      holdings: watchlistSymbol?.holding ?? null,
      financials: null,
      rankings: null,
      ratings: null,
      cryptoData: null,
      watchlistSymbol,
    };
  });

const buildQuotesBySymbolMap = (
  quotes: Awaited<ReturnType<QuotesManager['getDelayedQuotes']>>,
): Map<string, DelayedQuote> => {
  const quotesBySymbol = new Map<string, DelayedQuote>();
  Object.values(quotes?.ok ?? {}).forEach(quote => {
    let symbol = quote.symbol;
    if (quote.error) {
      symbol = quote.error.message.replace('Unknown symbol: ', '');
    }
    quotesBySymbol.set(normalizeWatchlistSymbol(symbol), quote);
  });
  return quotesBySymbol;
};

const fetchChunkQuotes = async (
  symbolChunk: string[],
  watchlist: Watchlist,
  session: Session,
): Promise<WatchlistFinancials[]> => {
  const quoteManager = session.getManager(QuotesManager);
  const quotesReq = quoteManager.getDelayedQuotes(symbolChunk);

  const cryptoBaseSymbols = [...new Set(symbolChunk.filter(isUSDCryptoSymbol).map(getCryptoBaseSymbol))];
  const cryptoCoinsReq =
    cryptoBaseSymbols.length > 0
      ? session.getManager(CryptoManager).getCoins(cryptoBaseSymbols)
      : Promise.resolve(null);

  const [quotes, cryptoCoins] = await Promise.all([quotesReq, cryptoCoinsReq]);

  if (quotes?.err) {
    logger.error('watchlist delayed quotes failed', quotes.err);
  }

  const cryptoCoinMap = new Map((cryptoCoins?.ok ?? []).map(coin => [coin.symbol?.toUpperCase(), coin]));
  const quotesBySymbol = buildQuotesBySymbolMap(quotes);

  return symbolChunk.map(symbol => {
    const watchlistSymbol = watchlist.symbols.find(symbolItem => symbolsMatch(symbolItem.symbol, symbol));
    return {
      symbol,
      quote: quotesBySymbol.get(normalizeWatchlistSymbol(symbol)) ?? null,
      holdings: watchlistSymbol?.holding ?? null,
      financials: null,
      rankings: null,
      ratings: null,
      cryptoData: cryptoCoinMap.get(getCryptoBaseSymbol(symbol)) ?? null,
      watchlistSymbol,
    };
  });
};

const fetchChunkQuotesWithRetry = async (
  symbolChunk: string[],
  watchlist: Watchlist,
  session: Session,
): Promise<WatchlistFinancials[]> => {
  try {
    return await fetchChunkQuotes(symbolChunk, watchlist, session);
  } catch (firstError) {
    logger.error(`Error fetching quote chunk for symbols: ${symbolChunk.join(', ')}; retrying`, firstError);
    try {
      return await fetchChunkQuotes(symbolChunk, watchlist, session);
    } catch (secondError) {
      logger.error(`Error fetching quote chunk after retry: ${symbolChunk.join(', ')}`, secondError);
      return buildPlaceholderFinancialRows(symbolChunk, watchlist);
    }
  }
};

const fetchChunkEnrichment = async (
  symbolChunk: string[],
  session: Session,
  rows: WatchlistFinancials[],
): Promise<WatchlistFinancials[]> => {
  const quoteManager = session.getManager(QuotesManager);
  const securitiesManager = session.getManager(SecuritiesManager);
  const calendarManager = session.getManager(CalendarManager);

  const tickerDetailsReq = getTickerDetailsWithRetry(quoteManager, symbolChunk);
  const financialsReq = securitiesManager.getFundamentals({ period: '3M', symbols: symbolChunk.join(',') });
  const ratingsReq = calendarManager.getCalendarData('ratings', ratingQuery(symbolChunk));

  const [tickerDetails, financials, ratings] = await Promise.all([tickerDetailsReq, financialsReq, ratingsReq]);

  if (financials?.err) {
    logger.error('watchlist fundamentals failed', financials.err);
  }
  if (ratings?.err) {
    logger.error('watchlist ratings failed', ratings.err);
  }

  const chunkSymbolSet = new Set(symbolChunk.map(symbol => normalizeWatchlistSymbol(symbol)));

  let enrichedRows = rows.map(row => {
    if (!chunkSymbolSet.has(normalizeWatchlistSymbol(row.symbol))) {
      return row;
    }

    return {
      ...row,
      financials: financials?.ok?.find(financial => symbolsMatch(financial.symbol, row.symbol)) ?? null,
      rankings: rankingsFromTickerDetails(tickerDetails, row.symbol),
      ratings: ratings?.ok?.find(rating => symbolsMatch(rating.ticker, row.symbol)) ?? null,
    };
  });

  const missingRankingSymbols = getSymbolsMissingRankings(symbolChunk, enrichedRows);
  if (missingRankingSymbols.length > 0 && !tickerDetails.err) {
    const gapFillDetails = await getTickerDetailsWithRetry(quoteManager, missingRankingSymbols);
    enrichedRows = mergeRankingsIntoRows(enrichedRows, gapFillDetails);
  }

  return enrichedRows;
};

const fetchChunkEnrichmentWithRetry = async (
  symbolChunk: string[],
  session: Session,
  rows: WatchlistFinancials[],
): Promise<WatchlistFinancials[]> => {
  try {
    return await fetchChunkEnrichment(symbolChunk, session, rows);
  } catch (firstError) {
    logger.error(`Error fetching enrichment chunk for symbols: ${symbolChunk.join(', ')}; retrying`, firstError);
    try {
      return await fetchChunkEnrichment(symbolChunk, session, rows);
    } catch (secondError) {
      logger.error(`Error fetching enrichment chunk after retry: ${symbolChunk.join(', ')}`, secondError);
      return rows;
    }
  }
};

/**
 * Fetches watchlist financials with progress tracking for large watchlists.
 * Chunks symbols into batches to avoid API timeouts and rate limits.
 * Shares in-flight + short TTL results across tables/insights.
 */
export const invalidateWatchlistFinancialsCache = (cacheKey?: string): void => {
  if (cacheKey) {
    watchlistQuotesCache.delete(`quotes:${cacheKey}`);
    watchlistEnrichmentCache.delete(`enrichment:${cacheKey}`);
    return;
  }
  watchlistQuotesCache.clear();
  watchlistEnrichmentCache.clear();
};

const loadWatchlistQuotesWithProgress = async (
  watchlist: Watchlist,
  onProgress: ((progress: { loaded: number; total: number; percent: number }) => void) | undefined,
  session: Session,
): Promise<WatchlistFinancials[]> => {
  try {
    const symbols = watchlist.symbols.map(symbol => symbol.symbol);
    const totalSymbols = symbols.length;

    if (totalSymbols <= SYMBOL_BATCH_SIZE) {
      const data = await fetchChunkQuotesWithRetry(symbols, watchlist, session);
      onProgress?.({ loaded: totalSymbols, total: totalSymbols, percent: 100 });
      return data;
    }

    const symbolChunks = chunkifyArray([...symbols], SYMBOL_BATCH_SIZE);
    const allData: WatchlistFinancials[] = [];
    let loadedSymbols = 0;

    for (let i = 0; i < symbolChunks.length; i += MAX_CONCURRENT_BATCHES) {
      const concurrentBatches = symbolChunks.slice(i, i + MAX_CONCURRENT_BATCHES);
      const batchResults = await Promise.all(
        concurrentBatches.map(async chunk => {
          const chunkData = await fetchChunkQuotesWithRetry(chunk, watchlist, session);
          loadedSymbols += chunk.length;
          onProgress?.({
            loaded: loadedSymbols,
            total: totalSymbols,
            percent: Math.round((loadedSymbols / totalSymbols) * 100),
          });
          return chunkData;
        }),
      );
      batchResults.forEach(chunkData => {
        allData.push(...chunkData);
      });
    }

    return allData;
  } catch (error) {
    logger.error('watchlist quotes', error);
    return [];
  }
};

export const getWatchlistQuotesWithProgress = async (
  watchlist: Watchlist,
  onProgress: ((progress: { loaded: number; total: number; percent: number }) => void) | undefined,
  session: Session,
): Promise<WatchlistFinancials[]> => {
  const cacheKey = getWatchlistQuotesCacheKey(watchlist);
  const cached = watchlistQuotesCache.get(cacheKey);
  if (cached?.value && Date.now() < cached.expiresAt) {
    onProgress?.({ loaded: cached.value.length, total: cached.value.length, percent: 100 });
    return cached.value;
  }
  if (cached?.promise) {
    return cached.promise;
  }

  const request = loadWatchlistQuotesWithProgress(watchlist, onProgress, session);
  watchlistQuotesCache.set(cacheKey, {
    expiresAt: Date.now() + WATCHLIST_QUOTES_TTL_MS,
    promise: request,
  });

  try {
    const value = await request;
    watchlistQuotesCache.set(cacheKey, {
      expiresAt: Date.now() + WATCHLIST_QUOTES_TTL_MS,
      value,
    });
    return value;
  } catch (error) {
    watchlistQuotesCache.delete(cacheKey);
    throw error;
  }
};

export const getWatchlistQuotes = async (watchlist: Watchlist, session: Session): Promise<WatchlistFinancials[]> => {
  return getWatchlistQuotesWithProgress(watchlist, undefined, session);
};

const loadWatchlistEnrichment = async (
  watchlist: Watchlist,
  baseRows: WatchlistFinancials[],
  session: Session,
): Promise<WatchlistFinancials[]> => {
  try {
    const symbols = watchlist.symbols.map(symbol => symbol.symbol);
    if (symbols.length === 0) return baseRows;

    if (symbols.length <= SYMBOL_BATCH_SIZE) {
      return fetchChunkEnrichmentWithRetry(symbols, session, baseRows);
    }

    const symbolChunks = chunkifyArray([...symbols], SYMBOL_BATCH_SIZE);
    let enrichedRows = baseRows;

    for (const chunk of symbolChunks) {
      enrichedRows = await fetchChunkEnrichmentWithRetry(chunk, session, enrichedRows);
    }

    return enrichedRows;
  } catch (error) {
    logger.error('watchlist enrichment', error);
    return baseRows;
  }
};

export const getWatchlistEnrichment = async (
  watchlist: Watchlist,
  baseRows: WatchlistFinancials[],
  session: Session,
): Promise<WatchlistFinancials[]> => {
  const cacheKey = getWatchlistEnrichmentCacheKey(watchlist);
  const cached = watchlistEnrichmentCache.get(cacheKey);
  if (cached?.value && Date.now() < cached.expiresAt) {
    return mergeQuoteLayerIntoRows(baseRows, cached.value);
  }
  if (cached?.promise) {
    const enriched = await cached.promise;
    return mergeQuoteLayerIntoRows(baseRows, enriched);
  }

  const request = loadWatchlistEnrichment(watchlist, baseRows, session);
  watchlistEnrichmentCache.set(cacheKey, {
    expiresAt: Date.now() + WATCHLIST_ENRICHMENT_TTL_MS,
    promise: request,
  });

  try {
    const value = await request;
    if (isWatchlistFinancialsDegraded(value, watchlist.symbols.length)) {
      watchlistEnrichmentCache.delete(cacheKey);
      return value;
    }
    watchlistEnrichmentCache.set(cacheKey, {
      expiresAt: Date.now() + WATCHLIST_ENRICHMENT_TTL_MS,
      value,
    });
    return value;
  } catch (error) {
    watchlistEnrichmentCache.delete(cacheKey);
    throw error;
  }
};

const mergeQuoteLayerIntoRows = (
  quoteRows: WatchlistFinancials[],
  enrichedRows: WatchlistFinancials[],
): WatchlistFinancials[] => {
  const quoteBySymbol = new Map(quoteRows.map(row => [normalizeWatchlistSymbol(row.symbol), row]));

  return enrichedRows.map(row => {
    const quoteRow = quoteBySymbol.get(normalizeWatchlistSymbol(row.symbol));
    if (!quoteRow) return row;

    return {
      ...row,
      quote: quoteRow.quote,
      holdings: quoteRow.holdings,
      cryptoData: quoteRow.cryptoData,
      watchlistSymbol: quoteRow.watchlistSymbol,
    };
  });
};

export const getWatchlistFinancialsWithProgress = async (
  watchlist: Watchlist,
  onProgress: ((progress: { loaded: number; total: number; percent: number }) => void) | undefined,
  session: Session,
): Promise<WatchlistFinancials[]> => {
  const quotes = await getWatchlistQuotesWithProgress(watchlist, onProgress, session);
  return getWatchlistEnrichment(watchlist, quotes, session);
};

export const getWatchlistFinancials = async (watchlist: Watchlist, session: Session) => {
  return getWatchlistFinancialsWithProgress(watchlist, undefined, session);
};

export const getDelayedQuotePrices = async (symbols: string[], session: Session): Promise<Record<string, number>> => {
  const holdingsData = await getDelayedHoldingQuotes(symbols, session);
  return holdingsData.pricesBySymbol;
};

export type DelayedHoldingQuotes = {
  allocationBySymbol: Record<string, string>;
  companyNamesBySymbol: Record<string, string>;
  pricesBySymbol: Record<string, number>;
};

type DelayedQuoteLike = {
  companyStandardName?: string | null;
  description?: string | null;
  lastTradePrice?: number | null;
  sector?: string | null;
  symbol?: string | null;
  type?: string | null;
};

const resolveHoldingCompanyName = (quote: DelayedQuoteLike): string | undefined => {
  if (quote.type === 'CRYPTO') {
    return quote.description?.trim() || undefined;
  }

  return quote.companyStandardName?.trim() || undefined;
};

export const mergeDelayedHoldingQuoteBatches = (
  batches: Array<{ ok?: Record<string, DelayedQuoteLike> | null } | null | undefined>,
): DelayedHoldingQuotes => {
  const pricesBySymbol: Record<string, number> = {};
  const allocationBySymbol: Record<string, string> = {};
  const companyNamesBySymbol: Record<string, string> = {};

  batches.forEach(batch => {
    Object.values(batch?.ok ?? {}).forEach(quote => {
      if (!quote.symbol) return;
      const normalizedSymbol = normalizeWatchlistSymbol(quote.symbol);
      if (Number.isFinite(quote.lastTradePrice) && quote.lastTradePrice) {
        pricesBySymbol[quote.symbol] = quote.lastTradePrice;
        pricesBySymbol[normalizedSymbol] = quote.lastTradePrice;
      }

      const allocationLabel = allocationLabelFromQuote(quote);
      allocationBySymbol[quote.symbol.toUpperCase()] = allocationLabel;
      allocationBySymbol[normalizedSymbol.toUpperCase()] = allocationLabel;

      const companyName = resolveHoldingCompanyName(quote);
      if (companyName) {
        companyNamesBySymbol[quote.symbol] = companyName;
        companyNamesBySymbol[normalizedSymbol] = companyName;
      }
    });
  });

  return { allocationBySymbol, companyNamesBySymbol, pricesBySymbol };
};

export const getMissingHoldingQuoteSymbols = (symbols: string[], pricesBySymbol: Record<string, number>): string[] =>
  symbols.filter(symbol => {
    const price = pricesBySymbol[symbol] ?? pricesBySymbol[normalizeWatchlistSymbol(symbol)];
    return !(typeof price === 'number' && price > 0);
  });

const mergeHoldingQuoteMaps = (
  primary: DelayedHoldingQuotes,
  secondary: DelayedHoldingQuotes,
): DelayedHoldingQuotes => ({
  allocationBySymbol: { ...primary.allocationBySymbol, ...secondary.allocationBySymbol },
  companyNamesBySymbol: { ...primary.companyNamesBySymbol, ...secondary.companyNamesBySymbol },
  pricesBySymbol: { ...primary.pricesBySymbol, ...secondary.pricesBySymbol },
});

export const getDelayedHoldingQuotes = async (symbols: string[], session: Session): Promise<DelayedHoldingQuotes> => {
  if (symbols.length === 0) return { allocationBySymbol: {}, companyNamesBySymbol: {}, pricesBySymbol: {} };

  const emptyQuotes: DelayedHoldingQuotes = {
    allocationBySymbol: {},
    companyNamesBySymbol: {},
    pricesBySymbol: {},
  };

  const loadQuotes = async (): Promise<DelayedHoldingQuotes> => {
    const quoteManager = session.getManager(QuotesManager);
    let quoteBatch = await quoteManager.getDelayedQuotes(symbols);
    if (quoteBatch.err) {
      logger.error('delayed holding quotes failed; retrying', quoteBatch.err);
      quoteBatch = await quoteManager.getDelayedQuotes(symbols);
    }

    let holdingsQuotes = mergeDelayedHoldingQuoteBatches([quoteBatch]);
    const missingSymbols = getMissingHoldingQuoteSymbols(symbols, holdingsQuotes.pricesBySymbol);

    if (missingSymbols.length > 0) {
      const missingBatch = await quoteManager.getDelayedQuotes(missingSymbols);
      holdingsQuotes = mergeHoldingQuoteMaps(holdingsQuotes, mergeDelayedHoldingQuoteBatches([missingBatch]));
    }

    return holdingsQuotes;
  };

  try {
    return await loadQuotes();
  } catch (error) {
    logger.error('delayed holding quotes', error);
    try {
      return await loadQuotes();
    } catch (retryError) {
      logger.error('delayed holding quotes retry', retryError);
      return emptyQuotes;
    }
  }
};

export type WatchlistDailyChange = DailyChangeResult;

const resolveDailyChangeFromQuoteRows = (
  watchlist: Watchlist,
  rows: WatchlistFinancials[],
): WatchlistDailyChange | null => {
  const rowBySymbol = new Map(rows.map(row => [normalizeWatchlistSymbol(row.symbol), row]));

  const positions = watchlist.symbols.flatMap(symbol => {
    const row = rowBySymbol.get(normalizeWatchlistSymbol(symbol.symbol));
    const position = buildDailyChangePosition({
      changePercent: row?.quote?.changePercent,
      costBasis: getHoldingCostBasis(symbol.holding),
      lastTradePrice: row?.quote?.lastTradePrice,
      previousClosePrice: row?.quote?.previousClosePrice,
      quantity: getHoldingQuantity(symbol.holding),
      symbol: symbol.symbol,
    });
    return position ? [position] : [];
  });

  return resolveDailyChangePercent(positions);
};

export const getWatchlistDailyChangePercent = async (
  watchlist: Watchlist,
  session: Session,
): Promise<WatchlistDailyChange | null> => {
  if (watchlist.symbols.length === 0) return null;

  try {
    const rows = await getWatchlistQuotes(watchlist, session);
    return resolveDailyChangeFromQuoteRows(watchlist, rows);
  } catch (error) {
    logger.error('watchlist daily change', error);
    return null;
  }
};

const COMPANY_DETAILS_TTL_MS = 60_000;
const companyDetailsCache = new Map<
  string,
  { expiresAt: number; promise?: Promise<CompanyDetails[]>; value?: CompanyDetails[] }
>();

const companyDetailsCacheKey = (symbols: string[]): string =>
  [...new Set(symbols.map(symbol => symbol.trim().toUpperCase()).filter(Boolean))].sort().join(',');

const fetchCompanyDetails = async (symbols: string[], session: Session): Promise<CompanyDetails[]> => {
  const insiderTradesManager = session.getManager(InsiderTradesManager);

  if (symbols.length <= SYMBOL_BATCH_SIZE) {
    const details = await insiderTradesManager.getCompanyDetails(symbols);
    return details?.ok ?? [];
  }

  const symbolChunks = chunkifyArray([...symbols], SYMBOL_BATCH_SIZE);
  const detailResults = await Promise.all(symbolChunks.map(chunk => insiderTradesManager.getCompanyDetails(chunk)));
  return detailResults.flatMap(result => result.ok ?? []);
};

export const getCompanyDetails = async (symbols: string[], session: Session): Promise<CompanyDetails[]> => {
  const uniqueSymbols = [...new Set(symbols.map(symbol => symbol.trim()).filter(Boolean))];
  if (uniqueSymbols.length === 0) return [];

  const cacheKey = companyDetailsCacheKey(uniqueSymbols);
  const cached = companyDetailsCache.get(cacheKey);
  if (cached?.value && Date.now() < cached.expiresAt) {
    return cached.value;
  }
  if (cached?.promise) {
    return cached.promise;
  }

  const promise = fetchCompanyDetails(uniqueSymbols, session);
  companyDetailsCache.set(cacheKey, { expiresAt: Date.now() + COMPANY_DETAILS_TTL_MS, promise });

  try {
    const value = await promise;
    companyDetailsCache.set(cacheKey, { expiresAt: Date.now() + COMPANY_DETAILS_TTL_MS, value });
    return value;
  } catch (error) {
    companyDetailsCache.delete(cacheKey);
    throw error;
  }
};

type HistoricalTickerSeries = {
  historical: Record<string, number>;
  staticCost?: number;
  symbol: string;
};

const normalizeHistoricalToMonths = (historical: Record<string, number> | undefined): Record<string, number> => {
  if (!historical) return {};

  return Object.entries(historical).reduce<Record<string, number>>((monthly, [date, value]) => {
    monthly[toMonthKey(date)] = value;
    return monthly;
  }, {});
};

const buildMergedHistoryChartOptions = (historicalData: HistoricalTickerSeries[]) => {
  const monthlySeries = historicalData.map(ticker => ({
    ...ticker,
    historical: normalizeHistoricalToMonths(ticker.historical),
  }));

  const uniqueDates = [...new Set(monthlySeries.flatMap(ticker => Object.keys(ticker.historical)))].sort();

  if (uniqueDates.length === 0) {
    return null;
  }

  const lineChartTickers = monthlySeries.map(ticker => ({
    areaStyle: {
      opacity: 1,
    },
    data: fillMonthlySeriesValues(uniqueDates, ticker.historical, ticker.staticCost ?? 0),
    name: ticker.symbol,
    stack: 'Total',
    type: 'line',
  }));

  const mergedData = lineChartTickers
    .reduce((aggregate, ticker) => {
      if (aggregate.length === 0) {
        return ticker.data.map(point => Number(point.toFixed(2)));
      }

      return ticker.data.map((point, index) => point + aggregate[index]);
    }, [] as number[])
    .map(sum => sum.toFixed(2));

  return generateHistoryLineChartOptions({ data: mergedData, dates: uniqueDates });
};

export const getWatchlistHistoricalChartOptions = async (data: WatchlistFinancials[], session: Session) => {
  if (data.length === 0) return null;

  const chartManager = session.getManager(ChartManager);
  const symbols = data.map(result => result.symbol);
  const threeYearsAgo = DateTime.now().minus({ years: 3 }).toFormat('yyyy-MM-dd');

  let allBars: Awaited<ReturnType<typeof chartManager.getWatchlistBars>>['ok'] = [];
  if (symbols.length <= SYMBOL_BATCH_SIZE) {
    const bars = await chartManager.getWatchlistBars(symbols, {
      from: threeYearsAgo,
      interval: '1MONTH',
    });
    allBars = bars.ok ?? [];
  } else {
    const symbolChunks = chunkifyArray([...symbols], SYMBOL_BATCH_SIZE);
    const barPromises = symbolChunks.map(chunk =>
      chartManager.getWatchlistBars(chunk, {
        from: threeYearsAgo,
        interval: '1MONTH',
      }),
    );
    const barResults = await Promise.all(barPromises);
    allBars = barResults.flatMap(result => result.ok ?? []);
  }

  if (allBars.length > 0) {
    const historicalData = data.map(ticker => {
      const historical = allBars.find(bar => bar.symbol === ticker.symbol);

      if (!historical) {
        return {
          historical: {},
          staticCost: getHoldingCostBasis(ticker.holdings),
          symbol: ticker.symbol,
        };
      }

      const quantity = getHistoryChartWeight(ticker.holdings);
      const candles = historical.candles?.reduce<Record<string, number>>((candleMap, candle) => {
        candleMap[toMonthKey(candle.dateTime.slice(0, 10))] = candle.close * quantity;
        return candleMap;
      }, {});

      return {
        historical: candles ?? {},
        symbol: ticker.symbol,
      };
    });

    return buildMergedHistoryChartOptions(historicalData);
  }

  // Local/proxy bars auth often returns []. Fall back to a small monthly sample of top holdings.
  const rankedTickers = [...data].sort(
    (first, second) => getHistoryChartWeight(second.holdings) - getHistoryChartWeight(first.holdings),
  );
  const chartTickers = rankedTickers.slice(0, 3);
  const chartsBySymbol = new Map<string, NonNullable<Awaited<ReturnType<typeof chartManager.getChart>>['ok']>>();

  const chartResults = await mapWithConcurrency(chartTickers, chartTickers.length, async ticker => {
    const chart = await chartManager.getChart({
      from: '1y',
      interval: '1M',
      symbol: ticker.symbol,
    });
    return { chart: chart.ok, symbol: ticker.symbol };
  });

  chartResults.forEach(result => {
    if (result.chart?.candles?.length) {
      chartsBySymbol.set(result.symbol, result.chart);
    }
  });

  if (chartsBySymbol.size === 0) {
    return null;
  }

  const historicalData = chartTickers.map(ticker => {
    const historical = chartsBySymbol.get(ticker.symbol);

    if (!historical) {
      return {
        historical: {},
        staticCost: getHoldingCostBasis(ticker.holdings),
        symbol: ticker.symbol,
      };
    }

    const quantity = getHistoryChartWeight(ticker.holdings);
    const candles = (historical.candles ?? []).reduce<Record<string, number>>((candleMap, candle) => {
      const dateTime = (candle.dateTime ?? '').toString();
      if (dateTime.length < 7) return candleMap;
      candleMap[toMonthKey(dateTime)] = (candle.close ?? 0) * quantity;
      return candleMap;
    }, {});

    return {
      historical: candles,
      symbol: ticker.symbol,
    };
  });

  return buildMergedHistoryChartOptions(historicalData);
};

export const getWatchlistInsiders = async (symbols: string[], session: Session) => {
  try {
    const insiderTrades = await getWatchlistInsiderFilingsFromSecCalendar(session, symbols);
    if (insiderTrades.err) {
      throw new Error('Error getting watchlist insiders');
    }
    return {
      all: insiderTrades.ok,
      buys: insiderTrades.ok.filter(filing => filing.trade_types.includes('p')),
      sells: insiderTrades.ok.filter(filing => filing.trade_types.includes('s')),
    };
  } catch (error) {
    console.log('Error getting watchlist insiders', error);
    return {
      all: [],
      buys: [],
      sells: [],
    };
  }
};

export const getWatchlistCalendars = async (symbols: string[], session: Session) => {
  try {
    const calendarManager = session.getManager(CalendarManager);

    if (symbols.length <= SYMBOL_BATCH_SIZE) {
      const earningsRequest = calendarManager.getCalendarData('earnings', {
        symbols: symbols.join(','),
        dateTo: DateTime.now().plus({ months: 3 }).toFormat('yyyy-MM-dd'),
      });
      const callsRequest = calendarManager.getCalendarData('conference', {
        symbols: symbols.join(','),
      });
      const dividendsRequest = calendarManager.getCalendarData('dividends', {
        symbols,
      });
      const splitsRequest = calendarManager.getCalendarData('splits', {
        symbols,
      });

      const [earnings, calls, dividends, splits] = await Promise.all([
        earningsRequest,
        callsRequest,
        dividendsRequest,
        splitsRequest,
      ]);

      return {
        earnings: earnings?.ok ?? [],
        conference: calls?.ok ?? [],
        dividends: dividends?.ok ?? [],
        splits: splits?.ok ?? [],
      };
    } else {
      const symbolChunks = chunkifyArray([...symbols], SYMBOL_BATCH_SIZE);
      const dateTo = DateTime.now().plus({ months: 3 }).toFormat('yyyy-MM-dd');

      const earningsPromises = symbolChunks.map(chunk =>
        calendarManager.getCalendarData('earnings', {
          symbols: chunk.join(','),
          dateTo,
        }),
      );
      const callsPromises = symbolChunks.map(chunk =>
        calendarManager.getCalendarData('conference', {
          symbols: chunk.join(','),
        }),
      );
      const dividendsPromises = symbolChunks.map(chunk =>
        calendarManager.getCalendarData('dividends', {
          symbols: chunk,
        }),
      );
      const splitsPromises = symbolChunks.map(chunk =>
        calendarManager.getCalendarData('splits', {
          symbols: chunk,
        }),
      );

      const [earningsResults, callsResults, dividendsResults, splitsResults] = await Promise.all([
        Promise.all(earningsPromises),
        Promise.all(callsPromises),
        Promise.all(dividendsPromises),
        Promise.all(splitsPromises),
      ]);

      return {
        earnings: earningsResults.flatMap(result => result.ok ?? []),
        conference: callsResults.flatMap(result => result.ok ?? []),
        dividends: dividendsResults.flatMap(result => result.ok ?? []),
        splits: splitsResults.flatMap(result => result.ok ?? []),
      };
    }
  } catch (error) {
    console.log('Error getting watchlist calendars', error);
    return {
      earnings: [],
      conference: [],
      dividends: [],
      splits: [],
    };
  }
};

export const getWatchlistPeers = async (symbols: string[], session: Session): Promise<WatchlistRelatedSymbol[]> => {
  try {
    const { getRelatedSymbolsForSymbols } = await import('@benzinga/scanner-manager');
    return await getRelatedSymbolsForSymbols(session, { symbols });
  } catch (error) {
    logger.error('watchlist peers', error);
    return [];
  }
};
